/**
 * IndexedDB key/value store — replaces localStorage for large inspection
 * payloads. Photo-heavy inspections blow the ~5MB localStorage quota
 * (QuotaExceededError seen in the field); IndexedDB holds gigabytes.
 * Every helper swallows errors — a local backup must never crash an inspection.
 */
const DB_NAME = "psc_local";
const STORE = "kv";
let dbPromise = null;

const openDb = () => {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(STORE);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }
  return dbPromise;
};

const run = async (mode, fn) => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const req = fn(t.objectStore(STORE));
    t.oncomplete = () => resolve(req?.result);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  });
};

export const kvGet = async (key) => { try { return await run("readonly", (s) => s.get(key)); } catch { return undefined; } };
export const kvSet = async (key, value) => { try { await run("readwrite", (s) => s.put(value, key)); return true; } catch { return false; } };
export const kvDel = async (key) => { try { await run("readwrite", (s) => s.delete(key)); } catch { /* noop */ } };
export const kvKeys = async () => { try { return (await run("readonly", (s) => s.getAllKeys())) || []; } catch { return []; } };

export const draftKey = (id) => `psc_inspection_draft_${id}`;
export const recoveryKey = (id) => `psc_inspection_recovery_${id}`;

/** Read a value — IndexedDB first, legacy localStorage entry as fallback. */
export const readLocal = async (key) => {
  const v = await kvGet(key);
  if (v !== undefined && v !== null) return v;
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

/** Remove a value from both IndexedDB and legacy localStorage. */
export const removeLocal = async (key) => {
  await kvDel(key);
  try { localStorage.removeItem(key); } catch { /* noop */ }
};
